// UserService connected to MongoDB backend
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

export interface User {
  _id?: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'teacher';
  allowedCourses: string[];
  allowedDimensions: number[];
  lastLogin?: string;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
  passwordExpiresAt?: string;
  mustChangePassword: boolean;
  passwordResetRequestedAt?: string | null; // pidió "Recuperar acceso" y espera clave temporal
  failedLoginAttempts: number;
  lockedUntil?: string;
  goals: UserGoal[];
}

export interface UserGoal {
  id: number;
  title: string;
  description: string;
  startDate: string;
  targetDate: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  progress: number;
  createdAt: string;
  assignedBy: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: 'admin' | 'teacher';
  allowedCourses: string[];
  allowedDimensions: number[];
  isActive: boolean;
  mustChangePassword: boolean;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: 'admin' | 'teacher';
  allowedCourses?: string[];
  allowedDimensions?: number[];
  isActive?: boolean;
  mustChangePassword?: boolean;
}

export interface PasswordPolicy {
  minLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  expirationDays: number;
  preventReuse: number;
  maxFailedAttempts: number;
  lockoutDuration: number;
}

// Get auth token from localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('token'); // Corregido: usar 'token' en lugar de 'authToken'
};

// Get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export class UserService {
  // Obtener todos los usuarios desde MongoDB
  static async getAllUsers(): Promise<User[]> {
    try {
      console.log('🔍 UserService.getAllUsers - Iniciando...');
      const token = getAuthToken();
      console.log('🔐 Token disponible:', token ? 'SÍ' : 'NO');
      console.log('📡 URL:', `${API_BASE_URL}/api/users`);
      
      const response = await axios.get(`${API_BASE_URL}/api/users`, {
        headers: getAuthHeaders()
      });
      
      console.log('📊 Response status:', response.status);
      console.log('📋 Response data structure:', {
        success: response.data.success,
        hasData: !!response.data.data,
        hasUsers: !!response.data.users,
        dataLength: response.data.data ? response.data.data.length : 0
      });
      
      if (response.data.success) {
        console.log('✅ UserService - Usuarios obtenidos exitosamente:', response.data.data.length);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al obtener usuarios');
      }
    } catch (error: any) {
      console.error('❌ Error al obtener usuarios:', error);
      console.error('📋 Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Obtener usuario por ID desde MongoDB
  static async getUserById(userId: string): Promise<User> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/${userId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Usuario no encontrado');
      }
    } catch (error: any) {
      console.error('Error al obtener usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Crear nuevo usuario en MongoDB
  static async createUser(userData: CreateUserData): Promise<User> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users`, userData, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al crear usuario');
      }
    } catch (error: any) {
      console.error('Error al crear usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Actualizar usuario en MongoDB
  static async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${userId}`, userData, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al actualizar usuario');
      }
    } catch (error: any) {
      console.error('Error al actualizar usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Eliminar usuario de MongoDB
  static async deleteUser(userId: string): Promise<void> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Error al eliminar usuario');
      }
    } catch (error: any) {
      console.error('Error al eliminar usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Activar/desactivar usuario
  static async toggleUserStatus(userId: string, isActive: boolean): Promise<User> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/api/users/${userId}/toggle-status`, 
        { isActive }, 
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          }
        }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al cambiar estado del usuario');
      }
    } catch (error: any) {
      console.error('Error al cambiar estado del usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Resetear contraseña
  static async resetPassword(userId: string): Promise<{ newPassword: string }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users/${userId}/reset-password`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        return { newPassword: response.data.newPassword };
      } else {
        throw new Error(response.data.message || 'Error al resetear contraseña');
      }
    } catch (error: any) {
      console.error('Error al resetear contraseña:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Cambiar contraseña a una específica
  static async changePassword(userId: string, newPassword: string): Promise<User> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/api/users/${userId}/change-password`, 
        { newPassword }, 
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        return response.data.user;
      } else {
        throw new Error(response.data.message || 'Error al cambiar contraseña');
      }
    } catch (error: any) {
      console.error('Error al cambiar contraseña:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Desbloquear usuario
  static async unlockUser(userId: string): Promise<User> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users/${userId}/unlock`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al desbloquear usuario');
      }
    } catch (error: any) {
      console.error('Error al desbloquear usuario:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Agregar meta a usuario
  static async addGoal(userId: string, goalData: Omit<UserGoal, 'id' | 'createdAt' | 'assignedBy'>): Promise<User> {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/users/${userId}/goals`, goalData, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al agregar meta');
      }
    } catch (error: any) {
      console.error('Error al agregar meta:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Actualizar progreso de meta
  static async updateGoalProgress(userId: string, goalId: number, progress: number): Promise<User> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/api/users/${userId}/goals/${goalId}`, 
        { progress }, 
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          }
        }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al actualizar progreso de meta');
      }
    } catch (error: any) {
      console.error('Error al actualizar progreso de meta:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Actualizar meta completa
  static async updateGoal(userId: string, goalId: number, goalData: Partial<UserGoal>): Promise<User> {
    try {
      const response = await axios.patch(`${API_BASE_URL}/api/users/${userId}/goals/${goalId}`, 
        goalData, 
        {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          }
        }
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al actualizar meta');
      }
    } catch (error: any) {
      console.error('Error al actualizar meta:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Eliminar meta
  static async deleteGoal(userId: string, goalId: number): Promise<User> {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/users/${userId}/goals/${goalId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Error al eliminar meta');
      }
    } catch (error: any) {
      console.error('Error al eliminar meta:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Importar usuarios desde archivo
  static async importUsers(file: File): Promise<{ imported: number; errors: string[] }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/api/users/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });
      
      if (response.data.success) {
        return response.data.results;
      } else {
        throw new Error(response.data.message || 'Error en la importación');
      }
    } catch (error: any) {
      console.error('Error al importar usuarios:', error);
      throw new Error(error.response?.data?.message || 'Error al conectar con el servidor');
    }
  }

  // Obtener política de contraseñas (mock implementation)
  static async getPasswordPolicy(): Promise<PasswordPolicy> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false,
          expirationDays: 90,
          preventReuse: 5,
          maxFailedAttempts: 5,
          lockoutDuration: 30
        });
      }, 300);
    });
  }

  // Actualizar política de contraseñas (mock implementation)
  static async updatePasswordPolicy(policy: PasswordPolicy): Promise<PasswordPolicy> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(policy);
      }, 500);
    });
  }

  // Validar datos de usuario
  static validateUserData(userData: CreateUserData | UpdateUserData): string[] {
    const errors: string[] = [];

    if ('username' in userData && userData.username) {
      if (userData.username.length < 3) {
        errors.push('El nombre de usuario debe tener al menos 3 caracteres');
      }
    }

    if ('email' in userData && userData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        errors.push('El formato del email no es válido');
      }
    }

    if ('firstName' in userData && userData.firstName) {
      if (userData.firstName.length < 2) {
        errors.push('El nombre debe tener al menos 2 caracteres');
      }
    }

    if ('lastName' in userData && userData.lastName) {
      if (userData.lastName.length < 2) {
        errors.push('El apellido debe tener al menos 2 caracteres');
      }
    }

    if ('password' in userData && userData.password) {
      if (userData.password.length < 6) {
        errors.push('La contraseña debe tener al menos 6 caracteres');
      }
    }

    if ('role' in userData && userData.role === 'teacher') {
      if ('allowedCourses' in userData && (!userData.allowedCourses || userData.allowedCourses.length === 0)) {
        errors.push('Los profesores deben tener al menos un curso asignado');
      }
    }

    return errors;
  }

  // Validar contraseña contra política
  static validatePassword(password: string, policy: PasswordPolicy): string[] {
    const errors: string[] = [];

    if (password.length < policy.minLength) {
      errors.push(`La contraseña debe tener al menos ${policy.minLength} caracteres`);
    }

    if (policy.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('La contraseña debe contener al menos una letra mayúscula');
    }

    if (policy.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('La contraseña debe contener al menos una letra minúscula');
    }

    if (policy.requireNumbers && !/\d/.test(password)) {
      errors.push('La contraseña debe contener al menos un número');
    }

    if (policy.requireSpecialChars && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('La contraseña debe contener al menos un carácter especial');
    }

    return errors;
  }

  // Generar contraseña segura
  static generateSecurePassword(): string {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    
    let password = '';
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    const allChars = uppercase + lowercase + numbers + special;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  }

  // Login function
  static async login(username: string, password: string): Promise<{ success: boolean; token?: string; username?: string; role?: string; allowedCourses?: string[]; allowedDimensions?: number[]; message?: string }> {
    try {
      console.log('🔐 Login attempt - URL:', `${API_BASE_URL}/api/login`, 'API_BASE_URL:', API_BASE_URL);
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        username,
        password
      });
      
      if (response.data.success) {
        // Store the token
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('username', response.data.username);
        localStorage.setItem('userRole', response.data.role);
        
        return {
          success: true,
          token: response.data.token,
          username: response.data.username,
          role: response.data.role,
          allowedCourses: response.data.allowedCourses,
          allowedDimensions: response.data.allowedDimensions
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Credenciales inválidas'
        };
      }
    } catch (error: any) {
      console.error('Error en login:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al conectar con el servidor'
      };
    }
  }

  // Logout function
  static logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const token = getAuthToken();
    return !!token;
  }

  // Get current user info from localStorage
  static getCurrentUser(): { username: string; role: string; allowedDimensions?: number[] } | null {
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('userRole');
    const allowedDimensions = localStorage.getItem('allowedDimensions');
    
    if (username && role) {
      const result: { username: string; role: string; allowedDimensions?: number[] } = { username, role };
      
      if (allowedDimensions) {
        try {
          result.allowedDimensions = JSON.parse(allowedDimensions);
        } catch (e) {
          console.error('Error parsing allowedDimensions from localStorage:', e);
        }
      }
      
      return result;
    }
    
    return null;
  }

  // Check connection
  static async checkConnection(): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/health`);
      return response.data.success;
    } catch (error) {
      console.error('Error al verificar conexión:', error);
      return false;
    }
  }
}

export default UserService;
 